/* eslint-disable sort-exports/sort-exports */
import { atom } from "jotai";
import { z } from "zod";

import { JotaiTool } from "../../index";

export const NeedsApiParams = z.object({
  zap_id: z
    .string()
    .describe("The unique identifier for the Zap containing the step."),
  step_id: z
    .string()
    .describe("The ID of the specific step to get field requirements for."),
  account_id: z
    .string()
    .describe(
      "The account ID owning the Zap (from additionalData.canvas.account_id).",
    ),
  selected_api: z
    .string()
    .describe(
      "REQUIRED: The app slug or full API identifier for the step (e.g., 'GmailCLIAPI@1.5.0' or 'gmail'). Obtain from get_zap if unsure.",
    ),
  action_id: z
    .string()
    .describe(
      "REQUIRED: The specific action key for the step (e.g., 'send_email'). Obtain from get_zap if unsure.",
    ),
  type_of_step: z
    .string()
    .describe(
      "REQUIRED: The type of the step (e.g., 'read', 'write', 'filter'). Obtain from get_zap if unsure.",
    ),
  current_params: z
    .record(z.any())
    .describe(
      "REQUIRED: The current parameters object for the step. Obtain from get_zap if unsure. This forms the base for the needs request.",
    ),
  authentication_id: z
    .number()
    .int()
    .optional()
    .describe(
      "Optional: The ID of the authentication to use for the step. Obtain from get_zap if applicable and unsure.",
    ),
  // Optional overrides for the final POST body to the downstream needs API
  override_body_params: z
    .record(z.any())
    .optional()
    .describe(
      "Optional: Key-value pairs to specifically override or add to the body sent to the downstream needs API. Use with caution.",
    ),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Getting field requirements...' or 'Loading step details...'",
    ),
});

export type NeedsApiParamsType = z.infer<typeof NeedsApiParams>;

interface NeedsField {
  choices?: unknown[];
  prefill?: string;
  [key: string]: unknown;
}

interface NeedsResponse {
  fields?: Record<string, NeedsField>;
  _info?: {
    fields_with_prefill?: Array<{ field: string; prefill: string }>;
  };
  [key: string]: unknown;
}

export const needsApiExecute = async (rawArgs: any) => {
  let params: NeedsApiParamsType;
  try {
    params = NeedsApiParams.parse(rawArgs || {});
  } catch (error) {
    console.error("Error parsing arguments for needsApi:", error);
    return {
      $error: {
        message: `Error: Invalid arguments provided for needsApi. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
      },
    };
  }

  const {
    zap_id,
    step_id,
    account_id,
    // These will go into the POST body to the proxy
    selected_api,
    action_id,
    type_of_step,
    current_params,
    authentication_id,
    override_body_params,
  } = params;

  const queryForProxy = new URLSearchParams();
  queryForProxy.append("zap_id", zap_id);
  queryForProxy.append("step_id", step_id);
  queryForProxy.append("account_id", account_id);

  const proxyApiUrl = `/api/canvasui/composer/needsApi?${queryForProxy.toString()}`;

  const bodyForProxy = {
    selected_api,
    action_id,
    type_of_step,
    current_params,
    ...(authentication_id !== undefined && { authentication_id }),
    ...(override_body_params && { override_body_params }),
  };

  try {
    const res = await fetch(proxyApiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(bodyForProxy),
    });

    if (!res.ok) {
      let errorDetailString = `Proxy error for needsApi (${res.status}).`;
      let responseDataForLog: any =
        "[Could not read error body after initial attempt]";
      try {
        const errorData = await res.json();
        responseDataForLog = errorData;
        if (errorData && errorData.error) {
          errorDetailString = `Proxy error for needsApi (${res.status}): ${errorData.error}${errorData.details ? ": " + JSON.stringify(errorData.details) : ""}`;
        } else {
          errorDetailString = `Proxy error for needsApi (${res.status}): Received non-standard JSON error from proxy: ${JSON.stringify(errorData)}`;
        }
      } catch {
        errorDetailString = `Proxy error for needsApi (${res.status}): Proxy error response was not valid JSON.`;
      }
      console.error(
        `[NEEDS_API_TOOL] Proxy error (${res.status}) for URL ${proxyApiUrl}. Details: ${JSON.stringify(responseDataForLog)}`,
      );
      throw new Error(errorDetailString);
    }

    try {
      const data: NeedsResponse = await res.json();
      return {
        content: [
          {
            type: "text",
            text: JSON.stringify(data, null, 2),
          },
        ],
      };
    } catch (e) {
      console.error(
        `[NEEDS_API_TOOL] Error parsing successful proxy response as JSON. URL: ${proxyApiUrl}, Status: ${res.status}. Error: ${e instanceof Error ? e.message : String(e)}`,
      );
      throw new Error(
        `Proxy returned success status (${res.status}) but its response body was not valid JSON.`,
      );
    }
  } catch (error) {
    console.error("[NEEDS_API_TOOL] Error in needsApiExecute:", error);
    return {
      $error: {
        message: `Error fetching step needs: ${error instanceof Error ? error.message : String(error)}`,
      },
    };
  }
};

export const needsApiDescription = `Get information about the required fields and parameters for a specific Zap step. 
IMPORTANT: This tool requires detailed information about the step. If you only know the Zap ID and Step ID, you MUST call the 'get_zap' tool first to retrieve the full Zap structure. Then, extract 'selected_api', 'action_id', 'type_of_step' (this is step.type), and 'current_params' (this is step.params) for the specific step from the 'get_zap' response and provide them as parameters to this tool. 
Required parameters: 
- zap_id: The ID of the Zap. 
- step_id: The ID of the specific step. 
- account_id: The ID of the account owning the Zap (MUST be from 'additionalData.canvas.account_id'). 
- selected_api: The app identifier for the step (e.g., 'GmailCLIAPI@1.5.0'). 
- action_id: The action key for the step. 
- type_of_step: The type of the step (e.g., 'read', 'write'). 
- current_params: The existing parameters object for the step. 
Optional parameters: 
- authentication_id: Authentication ID for the step, if known and applicable. 
- override_body_params: Key-value pairs to specifically override or add to the data sent to the downstream 'needs' API.`;

export const needsApiAtom = atom<JotaiTool<z.infer<typeof NeedsApiParams>>>(
  () => ({
    name: "needs_api",
    description: needsApiDescription,
    parameters: NeedsApiParams,
    execute: atom(
      null, // read function (not used for write-only atom)
      async (_get, _set, { args }) => {
        return needsApiExecute(args);
      },
    ),
  }),
);

needsApiAtom.debugLabel = "needsApiAtom";

/* eslint-enable sort-exports/sort-exports */
